import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import { getCaseStudyDetails } from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import PortfolioStyles from "./styles/Portfolio";
import { CASE_STUDIES } from "../constants/caseStudies";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { apiData, isLoading, error } = useFetch(getCaseStudyDetails, slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  console.log(slug);

  // Create a merged case study object - API data overrides defaults
  const caseStudy = CASE_STUDIES.find((cs) => cs.slug === slug) || {};

  useEffect(() => {
    document.title = `${caseStudy.title} - Case Study | Plural Code`;
  }, [caseStudy.title]);

  // Handle loading state
  if (isLoading) {
    return (
      <div className="project-detail-page loading">
        <div className="container text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading case study...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="project-detail-page error">
        <div className="container text-center py-5">
          <div className="alert alert-danger">
            <h4>Error loading case study</h4>
            <p>{error.message || "Failed to load case study details"}</p>
            <a href="/ourworks" className="btn btn-primary mt-3">
              Back to Case Studies
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      {/* Hero Section */}
      <section className="project-hero">
        <div className="container">
          <div className="hero-content">
            {/* Breadcrumb */}
            <div className="breadcrumb-nav">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li className="breadcrumb-item">
                    <a href="/ourworks">Case Studies</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {caseStudy.title}
                  </li>
                </ol>
              </nav>
            </div>

            {/* Badge */}
            <div className="hero-badge">
              <span className="badge-icon">🏭</span>
              <span className="badge-text">CASE STUDY</span>
            </div>

            {/* Title */}
            <h1 className="hero-title">{caseStudy.title}</h1>

            {/* Overview */}
            <p className="hero-subtitle">{caseStudy.excerpt}</p>

            {/* Client Info Grid */}
            <div className="client-info-grid">
              <div className="info-item">
                <div className="info-icon">🏢</div>
                <div className="info-content">
                  <div className="info-label">Client</div>
                  <div className="info-value">
                    {caseStudy.client.companyName}
                  </div>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">📍</div>
                <div className="info-content">
                  <div className="info-label">Location</div>
                  <div className="info-value">{caseStudy.client.location}</div>
                </div>
              </div>
              <div className="info-item">
                <div className="info-icon">⏱️</div>
                <div className="info-content">
                  <div className="info-label">Duration</div>
                  <div className="info-value">
                    {caseStudy.projectDetails.duration}
                  </div>
                </div>
              </div>
              {/* <div className="info-item">
                <div className="info-icon">💰</div>
                <div className="info-content">
                  <div className="info-label">Investment</div>
                  <div className="info-value">
                    {caseStudy.projectDetails.investment}
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Hero Image - Conditionally render */}
      {caseStudy.heroImage && (
        <section className="hero-image-section">
          <div className="container">
            <div className="hero-image-container">
              <img
                src={require(
                  `../assets/images/case-studies/${caseStudy.heroImage}`,
                )}
                alt={caseStudy.title}
                className="hero-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentNode.style.display = "none";
                }}
              />
              <div className="image-overlay">
                <div className="overlay-badge">Live Dashboard Preview</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quick Results */}
      <section className="results-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">📊</span>
              <span className="badge-text">KEY RESULTS</span>
            </div>
            <h2 className="section-title">Results at a Glance</h2>
            <p className="section-subtitle">
              Measurable impact delivered in just{" "}
              {caseStudy.projectDetails.duration}
            </p>
          </div>

          <div className="metrics-grid">
            {caseStudy.metricsAchieved.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-icon" style={{ color: metric.color }}>
                  {metric.icon}
                </div>
                <div className="metric-content">
                  <div className="metric-value" style={{ color: metric.color }}>
                    {metric.value}
                  </div>
                  <div className="metric-label">{metric.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Challenge */}
      <section className="challenge-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">⚡</span>
              <span className="badge-text">THE CHALLENGE</span>
            </div>
            <h2 className="section-title">Challenges</h2>
            <p className="section-subtitle">{caseStudy.challenges.join(" ")}</p>
          </div>

          {/* Pain Points */}
          <div className="pain-points-grid">
            {caseStudy.painPoints.map((pain, index) => (
              <div key={index} className="pain-point-card">
                <div className="pain-point-icon">{pain.icon}</div>
                <h3 className="pain-point-title">{pain.title}</h3>
                <p className="pain-point-description">{pain.description}</p>
              </div>
            ))}
          </div>

          {/* Challenge Points */}
          <div className="challenge-list">
            <h3 className="list-title">Key Issues Identified:</h3>
            <ul className="challenge-items">
              {caseStudy.keyIssuesIdentified.map((point, index) => (
                <li key={index} className="challenge-item">
                  <span className="item-icon">❌</span>
                  <span className="item-text" style={{ color: "#0a2458" }}>
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Quote */}
          <div className="quote-card">
            <div className="quote-icon">💬</div>
            <blockquote className="quote-text">
              "{caseStudy.problemStatement}"
            </blockquote>
            <div className="quote-author">
              <strong>{caseStudy.client.pocName}</strong>
              <span>{caseStudy.client.pocTitle}</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="solution-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">✨</span>
              <span className="badge-text">THE SOLUTION</span>
            </div>
            <h2 className="section-title">Solution Offered</h2>
            <p className="section-subtitle">
              {caseStudy.solutionOffered.overview}
            </p>
          </div>

          <div className="features-grid">
            {caseStudy.solutionOffered.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-header">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                </div>
                <p className="feature-description">{feature.description}</p>
                <ul className="feature-details">
                  {feature.details.map((detail, i) => (
                    <li key={i} className="detail-item">
                      <span className="detail-icon">✓</span>
                      <span
                        className="detail-text"
                        style={{ color: "#0a2458" }}
                      >
                        {detail}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots - Conditionally render */}
      {/* {caseStudy.screenshots.filter(
        (screenshot) => screenshot.image && screenshot.title !== "Not Available"
      ).length > 0 && (
        <section className="screenshots-section">
          <div className="container">
            <div className="section-header">
              <div className="section-badge">
                <span className="badge-icon">🖥️</span>
                <span className="badge-text">SYSTEM IN ACTION</span>
              </div>
              <h2 className="section-title">See It Working</h2>
              <p className="section-subtitle">
                Real screenshots from the deployed system
              </p>
            </div>

            {caseStudy.screenshots
              .filter(
                (screenshot) =>
                  screenshot.image &&
                  screenshot.title &&
                  screenshot.title !== "Not Available"
              )
              .map((screenshot, index) => (
                <div
                  key={index}
                  className={`screenshot-container ${
                    index % 2 === 1 ? "reversed" : ""
                  }`}
                >
                  <div className="screenshot-image">
                    <img
                      src={screenshot.image}
                      alt={screenshot.title}
                      className="screenshot-img"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentNode.style.display = "none";
                      }}
                    />
                    <div className="screenshot-overlay">
                      <span className="overlay-text">
                        Interactive Demo Available
                      </span>
                    </div>
                  </div>
                  <div className="screenshot-content">
                    <h3 className="screenshot-title">{screenshot.title}</h3>
                    <p className="screenshot-description">
                      {screenshot.description}
                    </p>
                    <div className="screenshot-badge">
                      <span className="tech-badge">React Dashboard</span>
                      <span className="tech-badge">Real-time Data</span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )} */}

      {/* Technical Implementation */}
      <section className="technical-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">🔧</span>
              <span className="badge-text">TECHNICAL DETAILS</span>
            </div>
            <h2 className="section-title">
              {caseStudy.projectTechnicalDetails.technicalHighlights.title}
            </h2>
          </div>

          <div className="technical-content">
            <div className="tech-stack">
              <h3 className="tech-title">Technology Stack</h3>
              <div className="stack-grid">
                {caseStudy.projectTechnicalDetails.technicalHighlights.stack.map(
                  (item, index) => (
                    <div key={index} className="stack-item">
                      <div className="stack-header">
                        <div className="stack-icon">⚙️</div>
                        <h4 className="stack-name">{item.tech}</h4>
                      </div>
                      <p
                        className="stack-description"
                        style={{ color: "#0a2458" }}
                      >
                        {item.use}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Results */}
      <section className="detailed-results-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">📈</span>
              <span className="badge-text">DETAILED IMPACT</span>
            </div>
            <h2 className="section-title">Impact Analysis</h2>
            <p className="section-subtitle">
              Comprehensive results across all business areas
            </p>
          </div>

          <div className="results-grid">
            {caseStudy.impacts.map((result, index) => (
              <div key={index} className="result-card">
                <h3 className="result-title">{result.key}</h3>
                <ul className="result-achievements">
                  {result.points.map((achievement, i) => (
                    <li key={i} className="achievement-item">
                      <span className="achievement-icon">✓</span>
                      <span
                        className="achievement-text"
                        style={{ color: "#0a2458" }}
                      >
                        {achievement}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">⏳</span>
              <span className="badge-text">PROJECT TIMELINE</span>
            </div>
            <h2 className="section-title">Our Development Process</h2>
            <p className="section-subtitle">
              From concept to deployment in 5 months
            </p>
          </div>

          <div className="timeline">
            {caseStudy.projectTechnicalDetails.timeline.map((phase, index) => (
              <div key={index} className="timeline-phase">
                <div className="phase-header">
                  <div className="phase-number">{index + 1}</div>
                  <div className="phase-title">
                    <h3>{phase.phase}</h3>
                    <span className="phase-duration">{phase.duration}</span>
                  </div>
                </div>
                <div className="phase-content">
                  <ul className="phase-activities">
                    {phase.activities.map((activity, i) => (
                      <li key={i} className="activity-item">
                        <span className="activity-icon">→</span>
                        <span className="activity-text">{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial-section">
        <div className="container">
          <div className="testimonial-card">
            <div className="testimonial-rating">
              <span className="star">⭐</span>
              <span className="star">⭐</span>
              <span className="star">⭐</span>
              <span className="star">⭐</span>
              <span className="star">⭐</span>
            </div>
            <blockquote className="testimonial-text">
              "{caseStudy.clientReview.quote}"
            </blockquote>
            <div className="testimonial-author">
              <div className="author-avatar">👤</div>
              <div className="author-info">
                <div className="author-name">
                  {caseStudy.clientReview.author}
                </div>
                <div className="author-role">
                  {caseStudy.clientReview.title} - {caseStudy.clientReview.date}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      {/* <section className="technologies-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">💻</span>
              <span className="badge-text">TECHNOLOGIES</span>
            </div>
            <h2 className="section-title">Built With Modern Tech</h2>
          </div>

          <div className="technologies-grid">
            {caseStudy.projectTechnicalDetails.technologies.map(
              (tech, index) => (
                <div key={index} className="technology-card">
                  <div className="tech-icon">{tech.logo}</div>
                  <div className="tech-name">{tech.name}</div>
                </div>
              )
            )}
          </div>
        </div>
      </section> */}

      {/* Related Projects */}
      <section className="related-projects-section">
        <div className="container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">🔗</span>
              <span className="badge-text">RELATED WORK</span>
            </div>
            <h2 className="section-title">Explore More Case Studies</h2>
            <p className="section-subtitle">
              Similar projects delivering exceptional results
            </p>
          </div>

          <div className="view-all">
            <a href="/ourworks" className="view-all-btn">
              View All Case Studies →
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">Ready for Similar Results?</h2>
              <p className="cta-subtitle">
                Every business is unique, but the principles are the same:
                understand the problem deeply, design around your actual
                workflows, and measure real business impact.
              </p>
              <div className="cta-buttons">
                <button
                  onClick={() => (window.location.href = "/contact")}
                  className="cta-primary"
                >
                  <span>Discuss Your Project</span>
                  <span>→</span>
                </button>
                <button
                  onClick={() => (window.location.href = "/ourworks")}
                  className="cta-secondary"
                >
                  <span>View More Case Studies</span>
                  <span>↗</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <PortfolioStyles />
    </div>
  );
}
